// BikeAnalytics.js with Multi-Tenant Firebase integration
import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, 
  ComposedChart, Area 
} from 'recharts';
import { 
  Calendar, Filter, RefreshCw, TrendingUp, Database, Building, 
  Users, Shield, AlertTriangle, Wifi, WifiOff, Info 
} from 'lucide-react';
import { db } from '../firebase';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp,
  addDoc,
  doc,
  getDoc,
  setDoc 
} from 'firebase/firestore';

// Colors for bike type pie chart
const COLORS = ['#FF8042', '#00C49F', '#FFBB28', '#0088FE', '#FF0066'];

// Multi-Tenant Firebase Analytics Service
class MultiTenantFirebaseAnalyticsService {
  constructor(companyId) {
    this.companyId = companyId;
    this.bookingsCollection = collection(db, 'bookings');
    this.bikesCollection = collection(db, 'managedBikes'); // Use managed bikes for multi-tenant
    this.analyticsCollection = collection(db, 'analytics');
  }

  // Validate company context
  validateCompanyContext() {
    if (!this.companyId) {
      throw new Error('Company ID is required for multi-tenant operations');
    }
  }

  // Get date range for queries
  getDateRange(rangeType, customRange = null) {
    const now = new Date();
    let startDate, endDate;

    switch (rangeType) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        break;
      case 'yesterday':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'last7Days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case 'last30Days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case 'thisMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = now;
        break;
      case 'lastMonth':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        startDate = lastMonth;
        endDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'custom':
        if (customRange) {
          startDate = new Date(customRange.startDate);
          endDate = new Date(customRange.endDate);
        } else {
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          endDate = now;
        }
        break;
      default:
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = now;
    }

    return { startDate, endDate };
  }

  // Fetch company-specific bookings within date range
  async fetchBookings(dateRange, customRange = null) {
    this.validateCompanyContext();
    
    try {
      const { startDate, endDate } = this.getDateRange(dateRange, customRange);
      
      console.log(`Fetching bookings for company ${this.companyId} from ${startDate} to ${endDate}`);
      
      const q = query(
        this.bookingsCollection,
        where('companyId', '==', this.companyId), // Multi-tenant filter
        where('bookingDate', '>=', Timestamp.fromDate(startDate)),
        where('bookingDate', '<=', Timestamp.fromDate(endDate)),
        orderBy('bookingDate', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const bookings = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        bookings.push({
          id: doc.id,
          ...data,
          bookingDate: data.bookingDate?.toDate(),
          startDate: data.startDate?.toDate(),
          endDate: data.endDate?.toDate()
        });
      });

      console.log(`Found ${bookings.length} bookings for company ${this.companyId}`);
      return bookings;
    } catch (error) {
      console.error('Error fetching company bookings:', error);
      throw error;
    }
  }

  // Fetch company-specific bikes data
  async fetchBikes() {
    this.validateCompanyContext();
    
    try {
      console.log(`Fetching bikes for company ${this.companyId}`);
      
      const q = query(
        this.bikesCollection,
        where('companyId', '==', this.companyId) // Multi-tenant filter
      );
      
      const querySnapshot = await getDocs(q);
      const bikes = [];

      querySnapshot.forEach((doc) => {
        bikes.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log(`Found ${bikes.length} bikes for company ${this.companyId}`);
      return bikes;
    } catch (error) {
      console.error('Error fetching company bikes:', error);
      throw error;
    }
  }

  // Calculate monthly rental data
  calculateMonthlyData(bookings) {
    const monthlyStats = {};
    const now = new Date();

    // Initialize last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toLocaleString('default', { month: 'short' });
      monthlyStats[monthKey] = {
        month: monthKey,
        rentals: 0,
        revenue: 0,
        totalDuration: 0,
        bookingCount: 0
      };
    }

    // Process bookings
    bookings.forEach(booking => {
      if (booking.bookingDate) {
        const monthKey = booking.bookingDate.toLocaleString('default', { month: 'short' });
        
        if (monthlyStats[monthKey]) {
          monthlyStats[monthKey].rentals += 1;
          monthlyStats[monthKey].revenue += booking.totalAmount || 0;
          
          // Calculate duration if dates are available
          if (booking.startDate && booking.endDate) {
            const duration = Math.ceil((booking.endDate - booking.startDate) / (1000 * 60 * 60 * 24));
            monthlyStats[monthKey].totalDuration += duration;
          }
          monthlyStats[monthKey].bookingCount += 1;
        }
      }
    });

    return Object.values(monthlyStats);
  }

  // Calculate bike type distribution
  calculateBikeTypeData(bookings, bikes) {
    const bikeTypeCount = {};

    // Create a map of bike IDs to their models
    const bikeModels = {};
    bikes.forEach(bike => {
      bikeModels[bike.id] = bike.model;
      bikeModels[bike.regNumber] = bike.model;
      bikeModels[`${bike.regNumber} - ${bike.model}`] = bike.model;
    });

    // Count bookings by bike type
    bookings.forEach(booking => {
      let model = 'Unknown';
      
      if (booking.bike) {
        model = bikeModels[booking.bike] || 
                bikeModels[booking.bikeId] || 
                'Unknown';
      }
      
      bikeTypeCount[model] = (bikeTypeCount[model] || 0) + 1;
    });

    // Convert to chart format
    return Object.entries(bikeTypeCount).map(([name, value]) => ({
      name,
      value
    }));
  }

  // Calculate top metrics
  calculateTopMetrics(bookings, bikes) {
    const totalRentals = bookings.length;
    const totalRevenue = bookings.reduce((sum, booking) => sum + (booking.totalAmount || 0), 0);
    
    // Calculate average duration
    const durationsArray = bookings
      .filter(booking => booking.startDate && booking.endDate)
      .map(booking => Math.ceil((booking.endDate - booking.startDate) / (1000 * 60 * 60 * 24)));
    
    const averageDuration = durationsArray.length > 0 
      ? (durationsArray.reduce((sum, duration) => sum + duration, 0) / durationsArray.length).toFixed(1)
      : 0;

    // Find most popular bike
    const bikeCount = {};
    bookings.forEach(booking => {
      if (booking.bike) {
        bikeCount[booking.bike] = (bikeCount[booking.bike] || 0) + 1;
      }
    });

    const popularBike = Object.keys(bikeCount).length > 0
      ? Object.keys(bikeCount).reduce((a, b) => bikeCount[a] > bikeCount[b] ? a : b)
      : 'No data available';

    return {
      totalRentals,
      averageDuration: parseFloat(averageDuration),
      revenue: totalRevenue,
      popularBike,
      totalBikes: bikes.length,
      activeBikes: bikes.filter(bike => bike.isActive !== false).length
    };
  }

  // Generate simple forecast based on historical data
  generateForecast(historicalData, months) {
    if (historicalData.length < 2) {
      return [];
    }

    const forecast = [];
    const lastMonths = historicalData.slice(-3); // Use last 3 months for trend
    
    // Calculate average growth rate
    let totalGrowthRate = 0;
    let validGrowthPoints = 0;

    for (let i = 1; i < lastMonths.length; i++) {
      if (lastMonths[i-1].rentals > 0) {
        const growthRate = (lastMonths[i].rentals - lastMonths[i-1].rentals) / lastMonths[i-1].rentals;
        totalGrowthRate += growthRate;
        validGrowthPoints++;
      }
    }

    const avgGrowthRate = validGrowthPoints > 0 ? totalGrowthRate / validGrowthPoints : 0.05; // Default 5% growth
    const lastMonth = historicalData[historicalData.length - 1];

    // Generate forecast months
    for (let i = 1; i <= months; i++) {
      const forecastRentals = Math.round(lastMonth.rentals * Math.pow(1 + avgGrowthRate, i));
      const forecastRevenue = Math.round(lastMonth.revenue * Math.pow(1 + avgGrowthRate, i));
      
      const date = new Date();
      date.setMonth(date.getMonth() + i);
      const monthName = date.toLocaleString('default', { month: 'short' });

      forecast.push({
        month: monthName,
        rentals: Math.max(0, forecastRentals),
        revenue: Math.max(0, forecastRevenue),
        isForecast: true
      });
    }

    return forecast;
  }

  // Save analytics to company-specific collection
  async saveAnalytics(analyticsData, companyInfo) {
    this.validateCompanyContext();
    
    try {
      const analyticsDoc = {
        ...analyticsData,
        companyId: this.companyId,
        companyName: companyInfo.companyName,
        lastUpdated: Timestamp.now(),
        dataSource: 'firebase',
        generatedBy: companyInfo.userId,
        generatedByUsername: companyInfo.username
      };

      const docId = `${this.companyId}_${analyticsData.dateRange}_${Date.now()}`;
      await setDoc(doc(this.analyticsCollection, docId), analyticsDoc);
      
      console.log(`Analytics saved for company ${this.companyId}`);
    } catch (error) {
      console.error('Error saving company analytics:', error);
    }
  }

  // Main method to get company-specific analytics data
  async getAnalyticsData(dateRange, customRange = null, forecastMonths = 3, companyInfo = {}) {
    this.validateCompanyContext();
    
    try {
      console.log(`Fetching analytics data for company ${this.companyId}...`);
      
      const [bookings, bikes] = await Promise.all([
        this.fetchBookings(dateRange, customRange),
        this.fetchBikes()
      ]);

      console.log(`Company ${this.companyId}: ${bookings.length} bookings, ${bikes.length} bikes`);

      const rentalData = this.calculateMonthlyData(bookings);
      const bikeTypeData = this.calculateBikeTypeData(bookings, bikes);
      const topMetrics = this.calculateTopMetrics(bookings, bikes);
      const forecastData = this.generateForecast(rentalData, forecastMonths);

      const analyticsResult = {
        companyId: this.companyId,
        dateRange,
        customRange,
        rentalData,
        bikeTypeData,
        topMetrics,
        forecastData
      };

      // Save analytics with company context
      await this.saveAnalytics(analyticsResult, companyInfo);

      return analyticsResult;
    } catch (error) {
      console.error(`Error getting analytics data for company ${this.companyId}:`, error);
      throw error;
    }
  }
}

// Mock data for initial state
const mockRentalData = [
  { month: 'Jan', rentals: 0, revenue: 0 },
  { month: 'Feb', rentals: 0, revenue: 0 },
  { month: 'Mar', rentals: 0, revenue: 0 },
  { month: 'Apr', rentals: 0, revenue: 0 },
  { month: 'May', rentals: 0, revenue: 0 },
  { month: 'Jun', rentals: 0, revenue: 0 },
];

const mockBikeTypeData = [
  { name: 'No Data', value: 100 }
];

const BikeAnalytics = ({
  // Multi-tenant props
  currentUser,
  currentCompany,
  getCompanyContext,
  hasPermission
}) => {
  // Get company context
  const companyId = getCompanyContext();
  
  // Initialize Multi-Tenant Firebase Analytics Service
  const [analyticsService, setAnalyticsService] = useState(null);
  
  // State variables
  const [dateRange, setDateRange] = useState('last30Days');
  const [isLoading, setIsLoading] = useState(false);
  const [forecastMonths, setForecastMonths] = useState(3);
  const [showForecast, setShowForecast] = useState(true);
  const [error, setError] = useState(null);
  const [firebaseStatus, setFirebaseStatus] = useState('unknown');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [analyticsData, setAnalyticsData] = useState({
    rentalData: mockRentalData,
    forecastData: [],
    bikeTypeData: mockBikeTypeData,
    topMetrics: {
      totalRentals: 0,
      averageDuration: 0,
      revenue: 0,
      popularBike: 'No data available',
      totalBikes: 0,
      activeBikes: 0
    }
  });

  // Permission check
  useEffect(() => {
    if (!hasPermission('bikeAnalytics')) {
      setError('You do not have permission to access Bike Analytics');
      setFirebaseStatus('permission_denied');
      return;
    }

    if (!companyId) {
      setError('No company context available');
      setFirebaseStatus('no_company');
      return;
    }

    // Initialize analytics service with company context
    const service = new MultiTenantFirebaseAnalyticsService(companyId);
    setAnalyticsService(service);
    
    // Check Firebase connection
    checkFirebaseConnection(service);
  }, [companyId, hasPermission]);

  // Check Firebase connection on component mount
  const checkFirebaseConnection = async (service) => {
    try {
      console.log(`Checking Firebase connection for company ${companyId}`);
      // Try to fetch bikes to test connection
      await service.fetchBikes();
      setFirebaseStatus('online');
      console.log('Firebase connection successful');
    } catch (error) {
      console.error('Firebase connection failed:', error);
      setFirebaseStatus('offline');
      setError('Firebase connection failed. Please check your internet connection.');
    }
  };
  
  // Function to fetch data from Firebase
  const getAnalyticsData = async (range) => {
    if (!analyticsService) {
      setError('Analytics service not initialized');
      return;
    }

    if (firebaseStatus === 'offline') {
      setError('Firebase is offline. Please check your connection.');
      return;
    }

    if (firebaseStatus === 'permission_denied') {
      setError('You do not have permission to access analytics data.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`Fetching analytics for company ${companyId}, range: ${range}`);
      
      const companyInfo = {
        companyId,
        companyName: currentCompany?.name || 'Unknown Company',
        userId: currentUser?.id || 'unknown',
        username: currentUser?.username || 'Unknown User'
      };
      
      const analyticsResponse = await analyticsService.getAnalyticsData(
        range, 
        range === 'custom' ? customDateRange : null,
        forecastMonths,
        companyInfo
      );
      
      setAnalyticsData(analyticsResponse);
      
    } catch (err) {
      console.error("Error in getAnalyticsData:", err);
      setError(`Failed to fetch analytics data: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Update forecast data when forecast months change
  const updateForecast = async (months) => {
    if (!analyticsService) {
      setError('Analytics service not initialized');
      return;
    }

    if (firebaseStatus === 'offline') {
      setError('Firebase is offline. Cannot update forecast.');
      return;
    }
    
    setForecastMonths(months);
    
    if (analyticsData.rentalData.length === 0) {
      return;
    }
    
    try {
      const forecastResponse = analyticsService.generateForecast(analyticsData.rentalData, months);
      
      setAnalyticsData(prev => ({
        ...prev,
        forecastData: forecastResponse
      }));
    } catch (error) {
      console.error("Error updating forecast:", error);
      setError(`Failed to update forecast: ${error.message}`);
    }
  };

  // Fetch data when analytics service is ready or date range changes
  useEffect(() => {
    if (analyticsService && firebaseStatus === 'online') {
      getAnalyticsData(dateRange);
    }
  }, [dateRange, analyticsService, firebaseStatus]);

  // Permission check render
  if (!hasPermission('bikeAnalytics')) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Shield className="mx-auto h-20 w-20 text-gray-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Access Denied</h3>
          <p className="text-gray-500 text-lg">You don't have permission to access Bike Analytics.</p>
          <p className="text-gray-600 text-sm mt-2">Contact your company administrator for access.</p>
        </div>
      </div>
    );
  }

  // Company context check
  if (!companyId) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-20 w-20 text-red-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">No Company Context</h3>
          <p className="text-gray-500 text-lg">Company context is required to access analytics.</p>
          <p className="text-gray-600 text-sm mt-2">Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-2 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
      {/* Company Context Header */}
      <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Building size={18} className="text-blue-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h4 className="text-white font-medium text-sm sm:text-base truncate">Company Analytics Dashboard</h4>
              <p className="text-gray-400 text-xs sm:text-sm truncate">
                Analytics for {currentCompany?.name || 'your company'} - data is completely isolated
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm">
            <div className="flex items-center gap-1 sm:gap-2 text-blue-400">
              <Users size={12} className="flex-shrink-0" />
              <span className="truncate">{currentUser?.username}</span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 text-green-400">
              <span className="font-medium">Multi-Tenant</span>
            </div>
          </div>
        </div>
      </div>

      {/* Firebase Status Indicator */}
      {firebaseStatus === 'offline' && (
        <div className="bg-red-800 border border-red-600 text-white p-3 rounded-lg mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-2">
          <WifiOff size={16} className="flex-shrink-0 mt-0.5 sm:mt-0" />
          <p className="text-sm sm:text-base">⚠️ Firebase connection failed. Please check your internet connection or Firebase configuration.</p>
        </div>
      )}

      {firebaseStatus === 'permission_denied' && (
        <div className="bg-red-800 border border-red-600 text-white p-3 rounded-lg mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-2">
          <Shield size={16} className="flex-shrink-0 mt-0.5 sm:mt-0" />
          <p className="text-sm sm:text-base">⚠️ You do not have permission to access analytics data.</p>
        </div>
      )}

      {firebaseStatus === 'no_company' && (
        <div className="bg-yellow-800 border border-yellow-600 text-white p-3 rounded-lg mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5 sm:mt-0" />
          <p className="text-sm sm:text-base">⚠️ No company context available. Please contact your administrator.</p>
        </div>
      )}
      
      {/* Display error message if there is one */}
      {error && (
        <div className="bg-red-800 border border-red-600 text-white p-3 rounded-lg mb-4">
          <p className="text-sm sm:text-base break-words">{error}</p>
          <button 
            className="mt-2 bg-red-600 hover:bg-red-700 text-white rounded px-3 py-1 text-sm transition-colors"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}
    
      {/* Header and Date Range Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4 sm:mb-6">
        <div className="min-w-0 flex-1">
          <h3 className="text-lg sm:text-xl font-bold text-white truncate">Company Analytics Dashboard</h3>
          <p className="text-gray-400 text-sm sm:text-base">Real-time insights from your company's Firebase data</p>
          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-1 text-xs sm:text-sm text-gray-500">
            <Building size={10} className="flex-shrink-0" />
            <span className="truncate">Company: {currentCompany?.name || 'Unknown'}</span>
            <span className="hidden sm:inline">•</span>
            <span className="text-xs truncate">ID: {companyId}</span>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <select 
            className="bg-gray-700 text-white rounded-lg px-3 py-2 border border-gray-600 text-sm min-w-0"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            disabled={firebaseStatus !== 'online' || isLoading}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7Days">Last 7 Days</option>
            <option value="last30Days">Last 30 Days</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="custom">Custom Range</option>
          </select>
          <button 
            className="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg px-3 py-2 border border-gray-600 text-sm transition-colors"
            onClick={() => getAnalyticsData(dateRange)}
            disabled={isLoading || firebaseStatus !== 'online'}
          >
            {isLoading ? (
              <span className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-orange-500"></span>
            ) : (
              <RefreshCw size={16} />
            )}
            <span className="hidden sm:inline">{isLoading ? 'Loading...' : 'Refresh'}</span>
          </button>
        </div>
      </div>
      
      {/* Forecast Controls */}
      <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 sm:mb-4 gap-3">
          <div className="flex items-center min-w-0 flex-1">
            <TrendingUp size={18} className="text-orange-500 mr-2 flex-shrink-0" />
            <h4 className="text-white font-bold text-sm sm:text-base truncate">Forecast Settings</h4>
            <div className="ml-2 sm:ml-3 flex items-center text-blue-400 text-xs sm:text-sm">
              <Building size={10} className="mr-1 flex-shrink-0" />
              <span className="truncate">Company-Specific</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-gray-400 text-xs sm:text-sm">Show forecast:</label>
            <input 
              type="checkbox" 
              checked={showForecast} 
              onChange={(e) => setShowForecast(e.target.checked)}
              className="bg-gray-600 border-gray-500"
              disabled={firebaseStatus !== 'online' || isLoading}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className="block text-gray-400 mb-2 text-xs sm:text-sm">Forecast Period (Months):</label>
            <div className="flex items-center gap-2 sm:gap-3">
              <input 
                type="range" 
                min="1" 
                max="12" 
                value={forecastMonths} 
                onChange={(e) => updateForecast(parseInt(e.target.value))}
                className="w-full accent-orange-500"
                disabled={firebaseStatus !== 'online' || isLoading}
              />
              <span className="text-white font-bold min-w-[2rem] text-center text-sm sm:text-base">{forecastMonths}</span>
            </div>
          </div>
          
          <div className="flex items-end">
            <button 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-3 sm:px-4 py-2 w-full text-sm sm:text-base transition-colors"
              onClick={() => updateForecast(forecastMonths)}
              disabled={firebaseStatus !== 'online' || analyticsData.rentalData.length === 0 || isLoading}
            >
              Update Forecast
            </button>
          </div>
        </div>
        
        <p className="text-xs sm:text-sm text-gray-400 mt-3">
          This forecasting uses historical trend analysis from your company's Firebase booking data to predict future rental patterns for the next {forecastMonths} month{forecastMonths > 1 ? 's' : ''}.
        </p>
      </div>

      {isLoading ? (
        <div className="flex flex-col justify-center items-center h-48 sm:h-64 space-y-4">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-orange-500"></div>
          <p className="text-gray-300 text-sm sm:text-base text-center">Loading analytics data from Firebase...</p>
          <p className="text-gray-400 text-xs sm:text-sm text-center">Company: {currentCompany?.name}</p>
        </div>
      ) : (
        <>
          {/* Top Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600">
              <p className="text-gray-400 text-xs sm:text-sm">Total Rentals</p>
              <h4 className="text-xl sm:text-2xl font-bold text-white">{analyticsData.topMetrics.totalRentals}</h4>
              <p className="text-xs text-gray-500 mt-1">Company-specific</p>
            </div>
            <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600">
              <p className="text-gray-400 text-xs sm:text-sm">Avg. Rental Duration</p>
              <h4 className="text-xl sm:text-2xl font-bold text-white">{analyticsData.topMetrics.averageDuration} days</h4>
              <p className="text-xs text-gray-500 mt-1">Based on bookings</p>
            </div>
            <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600">
              <p className="text-gray-400 text-xs sm:text-sm">Total Revenue</p>
              <h4 className="text-xl sm:text-2xl font-bold text-white">${analyticsData.topMetrics.revenue}</h4>
              <p className="text-xs text-gray-500 mt-1">Company earnings</p>
            </div>
            <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600">
              <p className="text-gray-400 text-xs sm:text-sm">Fleet Status</p>
              <h4 className="text-lg sm:text-xl font-bold text-white">
                {analyticsData.topMetrics.activeBikes}/{analyticsData.topMetrics.totalBikes}
              </h4>
              <p className="text-xs text-gray-500 mt-1">Active/Total bikes</p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
            {/* Monthly Rentals Chart with Forecast */}
            <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600">
              <h4 className="text-white font-bold mb-1 sm:mb-2 text-sm sm:text-base">Monthly Rentals with Forecast</h4>
              <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 truncate">Company: {currentCompany?.name}</p>
              <div className="h-64 sm:h-72 lg:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={showForecast && analyticsData.forecastData.length > 0
                      ? [...analyticsData.rentalData, ...analyticsData.forecastData] 
                      : analyticsData.rentalData}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                    <XAxis dataKey="month" stroke="#999" fontSize={12} />
                    <YAxis stroke="#999" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#333', border: '1px solid #555', fontSize: '12px' }} 
                      labelStyle={{ color: 'white' }}
                      itemStyle={{ color: 'white' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar 
                      dataKey="rentals" 
                      name="Actual Rentals" 
                      fill="#FF8042" 
                      isAnimationActive={true}
                      barSize={20}
                    />
                    {showForecast && analyticsData.forecastData.length > 0 && (
                      <Area 
                        type="monotone" 
                        dataKey="rentals" 
                        name="Forecast Rentals" 
                        fill="#FF804280"
                        stroke="#FF8042"
                        strokeDasharray="5 5"
                        dot={false}
                        activeDot={false}
                        isAnimationActive={true}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              {showForecast && analyticsData.forecastData.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="w-2 sm:w-3 h-2 sm:h-3 bg-orange-500"></div>
                    <span className="text-gray-400">Solid: Historical Data</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="w-2 sm:w-3 h-2 sm:h-3 border border-dashed border-orange-500"></div>
                    <span className="text-gray-400">Dashed: Forecast ({forecastMonths} months)</span>
                  </div>
                </div>
              )}
            </div>

            {/* Revenue Chart with Forecast */}
            <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600">
              <h4 className="text-white font-bold mb-1 sm:mb-2 text-sm sm:text-base">Monthly Revenue with Forecast</h4>
              <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 truncate">Company: {currentCompany?.name}</p>
              <div className="h-64 sm:h-72 lg:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={showForecast && analyticsData.forecastData.length > 0
                      ? [...analyticsData.rentalData, ...analyticsData.forecastData] 
                      : analyticsData.rentalData}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                    <XAxis dataKey="month" stroke="#999" fontSize={12} />
                    <YAxis stroke="#999" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#333', border: '1px solid #555', fontSize: '12px' }} 
                      labelStyle={{ color: 'white' }}
                      itemStyle={{ color: 'white' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      name="Actual Revenue" 
                      stroke="#00C49F" 
                      strokeWidth={2}
                      dot={{ fill: '#00C49F', r: 3 }}
                    />
                    {showForecast && analyticsData.forecastData.length > 0 && (
                      <Line 
                        type="monotone" 
                        dataKey="revenue" 
                        name="Forecast Revenue" 
                        stroke="#00C49F" 
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 3, fill: '#00C49F' }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {showForecast && analyticsData.forecastData.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="w-2 sm:w-3 h-2 sm:h-3 bg-green-500"></div>
                    <span className="text-gray-400">Solid: Historical Data</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="w-2 sm:w-3 h-2 sm:h-3 border border-dashed border-green-500"></div>
                    <span className="text-gray-400">Dashed: Forecast ({forecastMonths} months)</span>
                  </div>
                </div>
              )}
            </div>

            {/* Bike Type Distribution */}
            <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600">
              <h4 className="text-white font-bold mb-1 sm:mb-2 text-sm sm:text-base">Bike Type Distribution</h4>
              <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4">Company Fleet Analytics</p>
              <div className="h-64 sm:h-72 lg:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analyticsData.bikeTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius="80%"
                      fill="#8884d8"
                      dataKey="value"
                      fontSize={12}
                    >
                      {analyticsData.bikeTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#333', border: '1px solid #555', fontSize: '12px' }} 
                      labelStyle={{ color: 'white' }}
                      itemStyle={{ color: 'white' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Custom Date Range Selector (shows only when custom is selected) */}
            {dateRange === 'custom' && (
              <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600">
                <h4 className="text-white font-bold mb-3 sm:mb-4 text-sm sm:text-base">Custom Date Range</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-gray-400 mb-2 text-xs sm:text-sm">Start Date</label>
                    <input 
                      type="date" 
                      className="bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-600 w-full text-sm"
                      value={customDateRange.startDate}
                      onChange={(e) => setCustomDateRange({...customDateRange, startDate: e.target.value})}
                      disabled={firebaseStatus !== 'online' || isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-xs sm:text-sm">End Date</label>
                    <input 
                      type="date" 
                      className="bg-gray-800 text-white rounded-lg px-3 py-2 border border-gray-600 w-full text-sm"
                      value={customDateRange.endDate}
                      onChange={(e) => setCustomDateRange({...customDateRange, endDate: e.target.value})}
                      disabled={firebaseStatus !== 'online' || isLoading}
                    />
                  </div>
                  <button 
                    className="sm:col-span-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-3 py-2 mt-2 text-sm transition-colors"
                    onClick={() => getAnalyticsData('custom')}
                    disabled={firebaseStatus !== 'online' || isLoading}
                  >
                    Apply Custom Range
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Forecast Metrics */}
          {analyticsData.forecastData.length > 0 && (
            <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600 mt-4 sm:mt-6">
              <h4 className="text-white font-bold mb-3 sm:mb-4 flex flex-wrap items-center gap-2">
                <TrendingUp size={18} className="text-orange-500 flex-shrink-0" />
                <span className="text-sm sm:text-base">Forecast Metrics</span>
                <div className="flex items-center text-blue-400 text-xs sm:text-sm">
                  <Building size={10} className="mr-1 flex-shrink-0" />
                  <span className="truncate">{currentCompany?.name}</span>
                </div>
              </h4>
              
              {showForecast ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div className="bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-600">
                    <p className="text-gray-400 text-xs sm:text-sm">Projected Growth Rate</p>
                    <h4 className="text-xl sm:text-2xl font-bold text-orange-500">
                      {analyticsData.forecastData.length > 0 && analyticsData.rentalData.length > 0
                        ? (((analyticsData.forecastData[forecastMonths-1]?.rentals || 0) / 
                          analyticsData.rentalData[analyticsData.rentalData.length-1].rentals - 1) * 100).toFixed(1) + '%'
                        : 'N/A'
                      }
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">Over {forecastMonths} month period</p>
                  </div>
                  
                  <div className="bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-600">
                    <p className="text-gray-400 text-xs sm:text-sm">Estimated Revenue</p>
                    <h4 className="text-xl sm:text-2xl font-bold text-green-500">
                      ${analyticsData.forecastData.reduce((sum, item) => sum + item.revenue, 0).toLocaleString()}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">Total for forecast period</p>
                  </div>
                  
                  <div className="bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-600">
                    <p className="text-gray-400 text-xs sm:text-sm">Estimated Rentals</p>
                    <h4 className="text-xl sm:text-2xl font-bold text-blue-500">
                      {analyticsData.forecastData.reduce((sum, item) => sum + item.rentals, 0).toLocaleString()}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">Total for forecast period</p>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-600 text-center">
                  <p className="text-gray-300 text-sm sm:text-base">Enable forecast to see projected metrics</p>
                  <button 
                    className="mt-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-3 sm:px-4 py-2 text-sm sm:text-base transition-colors"
                    onClick={() => setShowForecast(true)}
                    disabled={isLoading}
                  >
                    Show Forecast
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Multi-Tenant Firebase Connection Status */}
          <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600 mt-4 sm:mt-6">
            <h4 className="text-white font-bold mb-3 sm:mb-4 flex items-center gap-2">
              <Database size={18} className="text-orange-500 flex-shrink-0" />
              <span className="text-sm sm:text-base">Multi-Tenant Firebase Status</span>
            </h4>
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center min-w-0 flex-1">
                  <div className={`w-2 sm:w-3 h-2 sm:h-3 rounded-full mr-2 flex-shrink-0 ${
                    firebaseStatus === 'online' ? 'bg-green-500' : 
                    firebaseStatus === 'offline' ? 'bg-red-500' : 
                    'bg-yellow-500'
                  }`}></div>
                  <span className="text-gray-300 text-sm sm:text-base truncate">
                    {firebaseStatus === 'online' ? 'Connected to Firebase' : 
                     firebaseStatus === 'offline' ? 'Firebase connection failed' : 
                     'Checking connection status...'}
                  </span>
                </div>
                
                <button 
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-lg px-3 sm:px-4 py-2 text-sm sm:text-base transition-colors"
                  onClick={() => window.location.reload()}
                  disabled={isLoading}
                >
                  Reconnect
                </button>
              </div>
              
              <div className="bg-gray-800 rounded-lg p-3">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm text-gray-400">
                  <div>
                    <div className="font-medium text-gray-300 mb-2">Data Source</div>
                    <div>• Firebase Firestore</div>
                    <div>• Collections: bookings, managedBikes, analytics</div>
                    <div>• Real-time updates: {firebaseStatus === 'online' ? 'Enabled' : 'Disabled'}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-300 mb-2">Multi-Tenant Context</div>
                    <div className="truncate">• Company: {currentCompany?.name || 'Unknown'}</div>
                    <div className="truncate">• Company ID: {companyId}</div>
                    <div className="truncate">• User: {currentUser?.username || 'Unknown'}</div>
                    <div>• Role: {currentUser?.role || 'Unknown'}</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 text-blue-400 mb-1">
                  <Info size={14} className="flex-shrink-0" />
                  <span className="font-medium text-sm sm:text-base">Multi-Tenant Data Isolation</span>
                </div>
                <p className="text-blue-300 text-xs sm:text-sm">
                  All analytics data is completely isolated by company. Only data from your company ({currentCompany?.name}) is included in these analytics.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BikeAnalytics;